/**
 * PortfolioChart - Historical portfolio value chart with benchmark comparison.
 *
 * Always shows data: benchmark index by default, portfolio overlaid when uploaded.
 * Supports absolute value view and normalized % comparison against SPY/QQQ.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceDot,
} from 'recharts';
import { Loader2, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import { useChartTheme } from '@/hooks/useChartTheme';

interface ChartDataPoint {
  date: string;
  value: number;
}

interface ChartResponse {
  data_points: ChartDataPoint[];
  benchmark_points: ChartDataPoint[];
  normalized_portfolio: ChartDataPoint[];
  normalized_benchmark: ChartDataPoint[];
  period_return_pct: number;
  benchmark_return_pct: number;
  benchmark_symbol: string;
  has_real_data: boolean;
  has_portfolio_data: boolean;
  portfolio_start_date: string | null;
  error?: string;
}

const TIME_RANGES = [
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '1Y', days: 365 },
  { label: '3Y', days: 1095 },
] as const;

const BENCHMARKS = [
  { label: 'S&P 500', value: 'SPY' },
  { label: 'Nasdaq', value: 'QQQ' },
] as const;

type ViewMode = 'absolute' | 'normalized';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPct(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function PortfolioChart() {
  const chart = useChartTheme();
  const [selectedRange, setSelectedRange] = useState(3); // Default to 1Y
  const [benchmark, setBenchmark] = useState<string>('SPY');
  const [viewMode, setViewMode] = useState<ViewMode>('normalized');
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [data, setData] = useState<ChartResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasPortfolio = data?.has_portfolio_data ?? false;
  // In benchmark-only mode, force normalized view (absolute benchmark prices aren't useful)
  const effectiveViewMode = hasPortfolio ? viewMode : 'normalized';

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const headers = getAuthHeaders();
    fetch(
      `${API_BASE_URL}/api/v1/portfolio/chart?days=${TIME_RANGES[selectedRange].days}&benchmark=${benchmark}`,
      { headers }
    )
      .then((res) => res.json())
      .then((res) => {
        if (!cancelled) {
          if (res.error && !res.has_real_data) {
            setError(res.error);
          }
          setData(res);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load chart data');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedRange, benchmark]);

  // Merge portfolio and benchmark data by date for the chart
  const mergedData = useMemo(() => {
    if (!data) return [];

    const benchmarkSeries = effectiveViewMode === 'normalized' ? data.normalized_benchmark : data.benchmark_points;

    if (hasPortfolio) {
      // Portfolio mode: merge portfolio + benchmark by date
      const portfolioSeries = effectiveViewMode === 'normalized' ? data.normalized_portfolio : data.data_points;
      if (!portfolioSeries?.length) return [];

      const dateMap = new Map<string, { date: string; portfolio: number; benchmark?: number }>();

      for (const p of portfolioSeries) {
        dateMap.set(p.date, { date: p.date, portfolio: p.value });
      }

      if (showBenchmark && benchmarkSeries?.length) {
        for (const b of benchmarkSeries) {
          const existing = dateMap.get(b.date);
          if (existing) {
            existing.benchmark = b.value;
          }
        }
      }

      return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    } else {
      // Benchmark-only mode: show benchmark as the primary series
      if (!benchmarkSeries?.length) return [];

      return benchmarkSeries.map((b) => ({
        date: b.date,
        benchmark: b.value,
      })).sort((a, b) => a.date.localeCompare(b.date));
    }
  }, [data, effectiveViewMode, showBenchmark, hasPortfolio]);

  // Determine primary return % and color
  const primaryReturn = hasPortfolio ? (data?.period_return_pct ?? 0) : (data?.benchmark_return_pct ?? 0);
  const isPositive = primaryReturn >= 0;
  const chartColor = isPositive ? chart.success : chart.error;
  const benchmarkColor = chart.text;

  // For benchmark-only mode, use benchmark return to color the line
  const benchmarkOnlyColor = (data?.benchmark_return_pct ?? 0) >= 0 ? chart.success : chart.error;

  // Find the starting point for portfolio overlay dot
  const startDot = useMemo(() => {
    if (!hasPortfolio || !mergedData.length) return null;
    const first = mergedData[0];
    if (first && 'portfolio' in first && first.portfolio !== undefined) {
      return { date: first.date, value: first.portfolio };
    }
    return null;
  }, [hasPortfolio, mergedData]);

  const benchmarkLabel = BENCHMARKS.find(b => b.value === benchmark)?.label ?? benchmark;

  return (
    <div className="rounded-xl border border-border bg-surface-light/30 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider">Performance</h3>
          {data && data.has_real_data && (
            <div className="flex items-center gap-3 mt-0.5">
              {hasPortfolio ? (
                <>
                  <span className={cn(
                    'text-lg font-semibold',
                    isPositive ? 'text-success' : 'text-error'
                  )}>
                    {formatPct(data.period_return_pct)}
                  </span>
                  {showBenchmark && data.benchmark_return_pct !== undefined && (
                    <span className="text-sm text-text-muted">
                      vs {data.benchmark_symbol} {formatPct(data.benchmark_return_pct)}
                    </span>
                  )}
                </>
              ) : (
                <span className={cn(
                  'text-lg font-semibold',
                  (data.benchmark_return_pct ?? 0) >= 0 ? 'text-success' : 'text-error'
                )}>
                  {formatPct(data.benchmark_return_pct)}
                  <span className="text-sm font-normal text-text-muted ml-2">
                    {benchmarkLabel}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle — only show when portfolio exists */}
          {hasPortfolio && (
            <div className="flex gap-0.5 bg-surface-light rounded-md p-0.5 hidden sm:flex">
              <button
                onClick={() => setViewMode('normalized')}
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                  viewMode === 'normalized'
                    ? 'bg-surface text-text-primary'
                    : 'text-text-muted hover:text-text-secondary'
                )}
              >
                %
              </button>
              <button
                onClick={() => setViewMode('absolute')}
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                  viewMode === 'absolute'
                    ? 'bg-surface text-text-primary'
                    : 'text-text-muted hover:text-text-secondary'
                )}
              >
                $
              </button>
            </div>
          )}

          {/* Benchmark selector */}
          <div className="flex gap-0.5 bg-surface-light rounded-md p-0.5 hidden sm:flex">
            {BENCHMARKS.map((b) => (
              <button
                key={b.value}
                onClick={() => { setBenchmark(b.value); setShowBenchmark(true); }}
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                  benchmark === b.value && showBenchmark
                    ? 'bg-surface text-text-primary'
                    : 'text-text-muted hover:text-text-secondary'
                )}
              >
                {b.label}
              </button>
            ))}
            {/* Off button — only show when portfolio exists (benchmark is the whole chart otherwise) */}
            {hasPortfolio && (
              <button
                onClick={() => setShowBenchmark(!showBenchmark)}
                className={cn(
                  'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                  !showBenchmark
                    ? 'bg-surface text-text-primary'
                    : 'text-text-muted hover:text-text-secondary'
                )}
              >
                Off
              </button>
            )}
          </div>

          {/* Time range pills */}
          <div className="flex gap-1">
            {TIME_RANGES.map((range, i) => (
              <button
                key={range.label}
                onClick={() => setSelectedRange(i)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                  selectedRange === i
                    ? 'bg-primary/20 text-primary border border-primary/30'
                    : 'text-text-muted hover:text-text-secondary hover:bg-surface-light'
                )}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[200px] sm:h-[280px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-text-muted animate-spin" />
          </div>
        ) : error && !data?.has_real_data ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <TrendingUp className="w-8 h-8 text-text-muted/30" />
            <p className="text-sm text-text-muted">Chart data unavailable</p>
            <p className="text-xs text-text-muted/60">Price data will populate after market data is fetched</p>
          </div>
        ) : mergedData.length < 2 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <TrendingUp className="w-8 h-8 text-text-muted/30" />
            <p className="text-sm text-text-muted">Chart data unavailable</p>
            <p className="text-xs text-text-muted/60">Market data could not be loaded</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={mergedData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColor} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="benchmarkGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={benchmarkOnlyColor} stopOpacity={0.12} />
                  <stop offset="95%" stopColor={benchmarkOnlyColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 11, fill: chart.text }}
                axisLine={false}
                tickLine={false}
                minTickGap={40}
              />
              <YAxis
                tickFormatter={(v) => effectiveViewMode === 'normalized' ? `${v}%` : formatCurrency(v)}
                tick={{ fontSize: 11, fill: chart.text }}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: chart.tooltipBg,
                  border: `1px solid ${chart.tooltipBorder}`,
                  borderRadius: '8px',
                  fontSize: '13px',
                  color: chart.tooltipText,
                }}
                labelFormatter={(label) => typeof label === 'string' ? formatDate(label) : ''}
                formatter={((value: number, name: string) => {
                  if (value == null) return ['—', name];
                  const label = name === 'portfolio' ? 'Portfolio' : (data?.benchmark_symbol || 'Benchmark');
                  if (effectiveViewMode === 'normalized') {
                    return [formatPct(value), label];
                  }
                  return [formatCurrency(value), label];
                }) as any}
              />

              {hasPortfolio ? (
                <>
                  {/* Portfolio mode: benchmark faded in background, portfolio primary */}
                  {showBenchmark && (
                    <Area
                      type="monotone"
                      dataKey="benchmark"
                      stroke={benchmarkColor}
                      strokeWidth={1}
                      strokeDasharray="4 4"
                      strokeOpacity={0.4}
                      fill="none"
                      dot={false}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="portfolio"
                    stroke={chartColor}
                    strokeWidth={1.5}
                    fill="url(#portfolioGradient)"
                    dot={false}
                  />
                  {/* Starting point dot */}
                  {startDot && (
                    <ReferenceDot
                      x={startDot.date}
                      y={startDot.value}
                      r={4}
                      fill={chartColor}
                      stroke={chart.activeDotStroke}
                      strokeWidth={2}
                    />
                  )}
                  {showBenchmark && (
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="line"
                      iconSize={10}
                      formatter={(value: string) =>
                        value === 'portfolio' ? 'Portfolio' : data?.benchmark_symbol || 'Benchmark'
                      }
                      wrapperStyle={{ fontSize: '11px', color: chart.text }}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Benchmark-only mode: benchmark as the primary colored line */}
                  <Area
                    type="monotone"
                    dataKey="benchmark"
                    stroke={benchmarkOnlyColor}
                    strokeWidth={1.5}
                    fill="url(#benchmarkGradient)"
                    dot={false}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="line"
                    iconSize={10}
                    formatter={() => benchmarkLabel}
                    wrapperStyle={{ fontSize: '11px', color: chart.text }}
                  />
                </>
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
